import { resolveComposerTimelineInset } from "../composerFooterLayout";
import { useCallback, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { ThreadComposerLayoutContext, type CiteAssistantText } from "./threadComposerLayoutContext";

/** Shares the floating Composer reservation between saved and connected threads. */
export function ThreadComposerLayout(props: {
  readonly children: ReactNode;
  readonly composer: ReactNode;
}) {
  const citationEditor = useRef<CiteAssistantText | null>(null);
  const registerCitationEditor = useCallback((handler: CiteAssistantText) => {
    citationEditor.current = handler;
    return () => {
      if (citationEditor.current === handler) citationEditor.current = null;
    };
  }, []);
  const citeAssistantText: CiteAssistantText = (citation, anchor) =>
    citationEditor.current?.(citation, anchor) ?? false;
  const overlayRef = useRef<HTMLDivElement>(null);
  const restingRef = useRef(false);
  const [measurement, setMeasurement] = useState({ overlayHeight: 0, timelineInset: 0 });

  const publishOverlayHeight = useCallback((height: number) => {
    const overlayHeight = Math.ceil(height);
    setMeasurement((previous) => {
      const timelineInset = resolveComposerTimelineInset({
        currentInset: previous.timelineInset,
        overlayHeight,
        isResting: restingRef.current,
      });
      return previous.overlayHeight === overlayHeight && previous.timelineInset === timelineInset
        ? previous
        : { overlayHeight, timelineInset };
    });
  }, []);

  useLayoutEffect(() => {
    const element = overlayRef.current;
    if (!element) return;
    const measure = () => publishOverlayHeight(element.getBoundingClientRect().height);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [publishOverlayHeight]);

  const setResting = (resting: boolean) => {
    restingRef.current = resting;
  };

  return (
    <ThreadComposerLayoutContext
      value={{
        ...measurement,
        setResting,
        publishOverlayHeight,
        registerCitationEditor,
        citeAssistantText,
      }}
    >
      <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
        {props.children}
        <div
          ref={overlayRef}
          data-chat-composer-overlay="true"
          className="pointer-events-none absolute inset-x-0 bottom-0 z-20 pb-[env(safe-area-inset-bottom)]"
        >
          <div className="pointer-events-auto">{props.composer}</div>
        </div>
      </div>
    </ThreadComposerLayoutContext>
  );
}
